import React, { useContext, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { faArrowUp, faEnvelope, faGlobe, faCode } from '@fortawesome/free-solid-svg-icons';
import { ScrollContext } from './ScrollContainer';
import './contact.css';

// Scroll storyboard: paper + name 0ms -> note 120ms -> tray 220ms -> stickers 300–600ms.
const TIMING = {
  note: .12,
  tray: .22,
  sticker: .3,
  stagger: .06
};
const SPRING = {
  type: 'spring',
  stiffness: 170,
  damping: 23
};
const EMAIL = 'hello@yashpunmiya.com';
const LINKS = [['Work', 'projects'], ['About', 'about'], ['Skills', 'skills'], ['Awards', 'achievements']];
const STICKERS = [{
  id: 'github',
  label: 'GitHub — open in a new tab',
  href: 'https://github.com/yashpunmiya',
  icon: faGithub,
  rotation: -9
}, {
  id: 'mail',
  label: 'Send Yash an email',
  href: `mailto:${EMAIL}`,
  icon: faEnvelope,
  rotation: 7
}, {
  id: 'star',
  label: 'Explore selected work',
  target: 'projects',
  rotation: 12
}, {
  id: 'code',
  label: 'Explore skills',
  target: 'skills',
  icon: faCode,
  rotation: -5
}, {
  id: 'globe',
  label: 'Learn about Yash',
  target: 'about',
  icon: faGlobe,
  rotation: -12
}, {
  id: 'make',
  label: 'Start a project by email',
  href: `mailto:${EMAIL}?subject=Let's%20make%20something%20real`,
  rotation: -13
}];
function Star() {
  return <span className="footer-star" aria-hidden="true">✳</span>;
}
function Sticker({
  item,
  trayRef,
  index,
  revealed,
  reduced,
  navigate
}) {
  const dragged = useRef(false);
  return <motion.a className={`footer-sticker sticker-${item.id}`} href={item.href || `#${item.target}`} target={item.id === 'github' ? '_blank' : undefined} rel={item.id === 'github' ? 'noopener noreferrer' : undefined} aria-label={item.label} aria-describedby="sticker-instructions" title={item.label} drag dragConstraints={trayRef} dragElastic={.08} dragMomentum={false} draggable={false} initial={reduced ? false : {
    opacity: 0,
    scale: .65,
    rotate: item.rotation - 16
  }} animate={{
    opacity: revealed ? 1 : 0,
    scale: 1,
    rotate: item.rotation
  }} transition={reduced ? {
    duration: 0
  } : {
    ...SPRING,
    delay: TIMING.sticker + index * TIMING.stagger
  }} whileHover={reduced ? undefined : {
    scale: 1.07,
    rotate: 0,
    zIndex: 30,
    transition: {
      ...SPRING,
      delay: 0
    }
  }} whileDrag={{
    scale: reduced ? 1 : 1.12,
    cursor: 'grabbing',
    zIndex: 50
  }} style={{
    zIndex: index + 1
  }} onPointerDown={() => {
    dragged.current = false;
  }} onDragStart={() => {
    dragged.current = true;
  }} onClick={event => {
    if (dragged.current && event.detail !== 0) {
      event.preventDefault();
      return;
    }
    if (item.target) navigate(event, item.target);
  }}>
    {item.icon && <FontAwesomeIcon icon={item.icon} />}{item.id === 'star' && <Star />}
    {item.id === 'make' && <span>Make<br />it real.</span>}{item.id === 'code' && <small>built with care</small>}
  </motion.a>;
}
export default function Contact() {
  const footerRef = useRef(null),
    trayRef = useRef(null);
  const {
    containerRef
  } = useContext(ScrollContext);
  const reduced = useReducedMotion();
  const inView = useInView(footerRef, {
    once: true,
    amount: .12,
    root: containerRef || undefined
  });
  const [resetKey, setResetKey] = useState(0);
  const {
    scrollYProgress
  } = useScroll({
    target: footerRef,
    container: containerRef || undefined,
    offset: ['start end', 'end end']
  });
  const artworkY = useTransform(scrollYProgress, [0, 1], [65, 0]);
  const revealed = reduced || inView;
  const reveal = (delay = 0) => ({
    initial: reduced ? false : {
      opacity: 0,
      y: 24
    },
    animate: {
      opacity: revealed ? 1 : 0,
      y: revealed ? 0 : 24
    },
    transition: reduced ? {
      duration: 0
    } : {
      ...SPRING,
      delay
    }
  });
  const navigate = (event, id) => {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'start'
    });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({
      preventScroll: true
    });
  };
  return <footer ref={footerRef} id="contact" className="atelier-footer" aria-label="Let's connect">
    <div className="footer-composition">
      <motion.nav className="footer-paper footer-directory" aria-label="Footer navigation" {...reveal()}>
        <span className="footer-pin" aria-hidden="true" /><span className="footer-eyebrow">A LITTLE DIRECTORY</span>
        {LINKS.map(([label, id], index) => <a key={id} href={`#${id}`} onClick={e => navigate(e, id)}><small>0{index + 1}</small><span>{label}</span><span className="footer-circle-arrow" aria-hidden="true">↗</span></a>)}
        <a href={`mailto:${EMAIL}`}><small>05</small><span>Contact</span><span className="footer-circle-arrow" aria-hidden="true">↗</span></a>
        <div className="footer-seal"><span>PURPOSE DRIVEN</span><Star /><span>IMPACT BY DESIGN</span></div>
      </motion.nav>
      <motion.div className="footer-name" {...reveal()}><span className="footer-eyebrow">A DEVELOPER. A MAKER. A LITTLE CURIOUS.</span><h2><span>Yash</span><span>Punmiya</span></h2></motion.div>
      <motion.div className="footer-artwork" aria-hidden="true" style={{
        y: reduced ? 0 : artworkY
      }}><img src={`${process.env.PUBLIC_URL}/assets/hand footer.png`} alt="" width="1254" height="1254" loading="lazy" draggable="false" /></motion.div>
      <motion.aside className="footer-note-wrap" {...reveal(TIMING.note)}><div className="footer-paper footer-note"><span className="footer-pin" aria-hidden="true" /><div className="footer-note-meta"><span>NOTE TO SELF</span><span>01 / ∞</span></div><p>Building digital<br />experiences that<br />feel human and<br />leave a mark.</p><Star /></div></motion.aside>
      <motion.div className="footer-focus" {...reveal(TIMING.note)}><span className="footer-eyebrow">THE FOCUS</span><Star /><p>Design.<br />Code.<br />Experience.</p><span className="footer-focus-line" /></motion.div>
      <motion.div className="footer-tray-wrap" {...reveal(TIMING.tray)}>
        <div className="footer-tray" ref={trayRef}><img className="footer-tray-image" src={`${process.env.PUBLIC_URL}/assets/metal plate.png`} alt="" width="1400" height="1067" loading="lazy" draggable="false" aria-hidden="true" />{STICKERS.map((item, index) => <Sticker key={`${resetKey}-${item.id}`} {...{
            item,
            index,
            trayRef,
            revealed,
            reduced,
            navigate
          }} />)}</div>
        <div className="footer-tray-caption"><span id="sticker-instructions">Drag to play. Click to explore.</span><button type="button" onClick={() => setResetKey(key => key + 1)} aria-label="Reset sticker positions">Reset ↺</button></div>
        <span className="footer-sr-only" role="status">{resetKey > 0 ? 'Stickers returned to their starting positions.' : ''}</span>
      </motion.div>
      <motion.a className="footer-plaque" href={`mailto:${EMAIL}?subject=Let's%20build%20something%20extraordinary`} {...reveal(TIMING.tray)} whileHover={reduced ? undefined : {
        y: -5
      }}><span className="footer-screw screw-left" aria-hidden="true" /><span className="footer-screw screw-right" aria-hidden="true" /><span>Let’s build something</span><strong>EXTRAORDINARY <span aria-hidden="true">↗</span></strong></motion.a>
    </div>
    <div className="footer-connect-row"><div><p>Good things start with a conversation. <Star /></p><a href={`mailto:${EMAIL}`}><FontAwesomeIcon icon={faEnvelope} />{EMAIL}<span className="footer-email-arrow" aria-hidden="true">↗</span></a></div><a className="footer-back-top" href="#hero" onClick={e => navigate(e, 'hero')} aria-label="Back to top"><span><FontAwesomeIcon icon={faArrowUp} /></span>Back to top</a></div>
    <div className="footer-colophon"><span>© {new Date().getFullYear()} Yash Punmiya</span><span>Designed & built with intention</span><span>Based in India <Star /></span><a href={`mailto:${EMAIL}`}><i aria-hidden="true" />Available for new projects ↗</a></div>
  </footer>;
}
